import { useEffect, useState } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import {
  Search, Heart, ShoppingCart, Sun, Moon, Menu, X,
  ChevronDown, User, BookOpen, Mic, LogOut, PenSquare, Tag, Crown,
} from 'lucide-react';
import clsx from 'clsx';
import { useTheme } from '../../context/ThemeContext';
import { useAuthStore } from '../../context/useAuthStore';
import { useCartStore } from '../../context/useCartStore';
import { useWishlistStore } from '../../context/useWishlistStore';
import { api } from '../../lib/api';
import { normalizeBooks } from '../../lib/normalizers';
import { buildSuggestions } from '../../lib/searchSuggestions';
import { useGenres } from '../../hooks/useGenres';
import MobileSidebar from './MobileSidebar';

const SUGGESTION_ICONS = { book: BookOpen, author: PenSquare, genre: Tag };

const baseNavLinks = [
  { label: 'Home', to: '/' },
  { label: 'Shop', to: '/shop', mega: true },
  { label: 'Authors', to: '/authors' },
  { label: 'Membership', to: '/membership' },
  { label: 'Blog', to: '/blog' },
];

function getNavLinks(user) {
  const isAuthor = user?.role === 'author' || user?.pendingAuthorUpgrade;
  const publishOrLibrary = isAuthor
    ? { label: 'Publish', to: '/publish' }
    : user
      ? { label: 'Library', to: '/library' }
      : { label: 'Publish', to: '/publish' };

  return [
    ...baseNavLinks.slice(0, 4),
    publishOrLibrary,
    baseNavLinks[4],
  ];
}

export default function Navbar() {
  const { theme, toggleTheme } = useTheme();
  const [scrolled, setScrolled] = useState(false);
  const [megaOpen, setMegaOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [suggestionsOpen, setSuggestionsOpen] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [checkingSearch, setCheckingSearch] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const navigate = useNavigate();

  const genres = useGenres({ withBooks: true });
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const cartCount = useCartStore((s) => s.items.length);
  const wishlistCount = useWishlistStore((s) => s.bookIds.length);
  const navLinks = getNavLinks(user);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    if (userMenuOpen) {
      const close = () => setUserMenuOpen(false);
      document.addEventListener('click', close);
      return () => document.removeEventListener('click', close);
    }
  }, [userMenuOpen]);

  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) {
      setSuggestions([]);
      setSuggestionsOpen(false);
      return;
    }
    let cancelled = false;
    const controller = new AbortController();
    const timer = setTimeout(async () => {
      try {
        const data = await api.get(`/books/suggest?q=${encodeURIComponent(q)}&limit=8`, { signal: controller.signal });
        if (cancelled) return;
        setSuggestions(buildSuggestions(q, normalizeBooks(data.books), genres));
        setSuggestionsOpen(true);
      } catch {
        if (!cancelled) {
          setSuggestions([]);
          setSuggestionsOpen(false);
        }
      }
    }, 300);
    return () => {
      cancelled = true;
      controller.abort();
      clearTimeout(timer);
    };
  }, [query, genres]);

  const goToSuggestion = (suggestion) => {
    navigate(suggestion.to);
    setQuery('');
    setSuggestions([]);
    setSuggestionsOpen(false);
    setSearchError('');
    setSearchOpen(false);
  };

  // Checks for at least one match before leaving the navbar — a query with
  // zero results shows an inline error here instead of landing the user on
  // an empty /shop page.
  const runSearch = async (q) => {
    if (!q || checkingSearch) return;
    setSearchError('');
    setSuggestionsOpen(false);
    setCheckingSearch(true);
    try {
      const data = await api.get(`/books?q=${encodeURIComponent(q)}&limit=1`);
      if (!data.pagination?.total) {
        setSearchError(`No results found for "${q}"`);
        return;
      }
      navigate(`/shop?q=${encodeURIComponent(q)}`);
      setSearchOpen(false);
    } catch {
      setSearchError('Something went wrong. Please try again.');
    } finally {
      setCheckingSearch(false);
    }
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    runSearch(query.trim());
  };

  const handleVoiceSearch = () => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      alert('Voice search is not supported in this browser.');
      return;
    }
    const recognition = new SpeechRecognition();
    recognition.lang = 'en-US';
    recognition.onresult = (event) => {
      const transcript = event.results[0][0].transcript;
      setQuery(transcript);
      runSearch(transcript.trim());
    };
    recognition.start();
  };

  const handleLogout = async () => {
    await logout();
    setUserMenuOpen(false);
    const redirectUrl =
      sessionStorage.getItem('redirectAfterLogin') || '/';

    sessionStorage.removeItem('redirectAfterLogin');

    navigate(redirectUrl, {
      replace: true,
    });
  };

  return (
    <>
      <header
        className={clsx(
          'sticky top-0 z-50 transition-all duration-300',
          scrolled
            ? 'glass shadow-soft'
            : 'bg-surface-light/80 dark:bg-surface-dark/80 backdrop-blur-md'
        )}
      >
        <div className="container-app flex h-16 items-center justify-between gap-4">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 shrink-0">
            <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-primary text-white shadow-glow">
              <img src="/logo.png" alt="Book Cloud" className="h-9 w-9 rounded-xl object-cover" />
            </span>
            <span className="font-display text-xl font-semibold tracking-tight text-slate-900 dark:text-white">
              Book Cloud
            </span>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => (
              <div
                key={link.to}
                className="relative"
                onMouseEnter={() => link.mega && setMegaOpen(true)}
                onMouseLeave={() => link.mega && setMegaOpen(false)}
              >
                <NavLink
                  to={link.to}
                  className={({ isActive }) =>
                    clsx(
                      'flex items-center gap-1 rounded-full px-4 py-2 text-sm font-medium transition-colors',
                      isActive
                        ? 'text-primary-700 dark:text-primary-300'
                        : 'text-slate-600 dark:text-slate-300 hover:text-primary-600 dark:hover:text-primary-300'
                    )
                  }
                >
                  {link.label}
                  {link.mega && <ChevronDown size={14} />}
                </NavLink>

                <AnimatePresence>
                  {link.mega && megaOpen && genres.length > 0 && (
                    <motion.div
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 8 }}
                      transition={{ duration: 0.18 }}
                      className="absolute left-1/2 top-full z-40 mt-2 w-[560px] -translate-x-1/2 rounded-xl2 border border-slate-100 dark:border-white/10 bg-white dark:bg-surface-darkCard p-6 shadow-soft-lg grid grid-cols-2 gap-x-8 gap-y-3"
                    >
                      {genres.map((g) => (
                        <Link
                          key={g}
                          to={`/shop?genre=${encodeURIComponent(g)}`}
                          className="text-sm text-slate-600 dark:text-slate-300 hover:text-primary-600 dark:hover:text-primary-300 transition-colors"
                        >
                          {g}
                        </Link>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </nav>

          {/* Right actions */}
          <div className="flex items-center gap-1 sm:gap-2">
            <div className="relative hidden sm:block">
              <AnimatePresence>
                {searchOpen ? (
                  <motion.form
                    initial={{ width: 40, opacity: 0 }}
                    animate={{ width: 260, opacity: 1 }}
                    exit={{ width: 40, opacity: 0 }}
                    onSubmit={handleSearchSubmit}
                    className="relative flex items-center gap-1 rounded-full border border-slate-200 dark:border-white/10 bg-white dark:bg-white/5 px-3 py-2"
                  >
                    <Search size={16} className="text-slate-400 shrink-0" />
                    <input
                      autoFocus
                      value={query}
                      onChange={(e) => { setQuery(e.target.value); setSearchError(''); }}
                      onFocus={() => suggestions.length > 0 && setSuggestionsOpen(true)}
                      onBlur={() => {
                        setTimeout(() => setSuggestionsOpen(false), 150);
                        if (!query) setSearchOpen(false);
                      }}
                      placeholder="Search titles, authors, genres, ID..."
                      className="w-full bg-transparent text-sm outline-none placeholder:text-slate-400"
                    />
                    <button type="button" onClick={handleVoiceSearch} aria-label="Voice search" title="Voice search">
                      <Mic size={15} className="text-slate-400 hover:text-primary-500" />
                    </button>

                    <AnimatePresence>
                      {searchError && (
                        <motion.p
                          initial={{ opacity: 0, y: 6 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: 6 }}
                          transition={{ duration: 0.15 }}
                          className="absolute left-0 top-full z-40 mt-2 w-full min-w-[280px] rounded-xl2 border border-red-200 dark:border-red-900/40 bg-white dark:bg-surface-darkCard px-3 py-2.5 text-xs text-red-500 shadow-soft-lg"
                        >
                          {searchError}
                        </motion.p>
                      )}
                    </AnimatePresence>

                    <AnimatePresence>
                      {suggestionsOpen && suggestions.length > 0 && (
                        <motion.ul
                          initial={{ opacity: 0, y: 6 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: 6 }}
                          transition={{ duration: 0.15 }}
                          className="absolute left-0 top-full z-40 mt-2 w-full min-w-[280px] overflow-hidden rounded-xl2 border border-slate-100 dark:border-white/10 bg-white dark:bg-surface-darkCard shadow-soft-lg"
                        >
                          {suggestions.map((s) => {
                            const Icon = SUGGESTION_ICONS[s.type];
                            return (
                              <li key={`${s.type}-${s.label}`}>
                                <button
                                  type="button"
                                  onMouseDown={(e) => e.preventDefault()}
                                  onClick={() => goToSuggestion(s)}
                                  className="flex w-full items-center gap-3 px-3 py-2.5 text-left text-sm hover:bg-primary-50 dark:hover:bg-white/5"
                                >
                                  <Icon size={15} className="shrink-0 text-slate-400" />
                                  <span className="min-w-0 flex-1 truncate text-slate-700 dark:text-slate-200">{s.label}</span>
                                  {s.sub && (
                                    <span className="shrink-0 text-xs text-slate-400">{s.sub}</span>
                                  )}
                                </button>
                              </li>
                            );
                          })}
                        </motion.ul>
                      )}
                    </AnimatePresence>
                  </motion.form>
                ) : (
                  <button
                    onClick={() => setSearchOpen(true)}
                    aria-label="Search"
                    title="Search"
                    className="flex h-10 w-10 items-center justify-center rounded-full text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-white/10"
                  >
                    <Search size={19} />
                  </button>
                )}
              </AnimatePresence>
            </div>

            <button
              onClick={toggleTheme}
              aria-label="Toggle dark mode"
              title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
              className="flex h-10 w-10 items-center justify-center rounded-full text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-white/10"
            >
              {theme === 'dark' ? <Sun size={19} /> : <Moon size={19} />}
            </button>

            <Link
              to="/account/wishlist"
              aria-label="Wishlist"
              title="Wishlist"
              className="relative hidden sm:flex h-10 w-10 items-center justify-center rounded-full text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-white/10"
            >
              <Heart size={19} />
              {wishlistCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-accent text-[10px] font-bold text-white">
                  {wishlistCount}
                </span>
              )}
            </Link>

            <Link
              to="/cart"
              aria-label="Cart"
              title="Cart"
              className="relative flex h-10 w-10 items-center justify-center rounded-full text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-white/10"
            >
              <ShoppingCart size={19} />
              {cartCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-primary-600 text-[10px] font-bold text-white">
                  {cartCount}
                </span>
              )}
            </Link>

            {/* User menu */}
            {user ? (
              <div className="relative hidden md:block">
                <button
                  onClick={(e) => { e.stopPropagation(); setUserMenuOpen((o) => !o); }}
                  title={user.name}
                  className="flex h-10 w-10 items-center justify-center rounded-full overflow-hidden ring-2 ring-primary-100 dark:ring-primary-900/40 hover:ring-primary-300 dark:hover:ring-primary-600 transition-all"
                >
                  {user.avatar ? (
                    <img src={user.avatar} alt={user.name} className="h-full w-full object-cover" />
                  ) : (
                    <span className="flex h-full w-full items-center justify-center bg-gradient-primary text-white text-sm font-semibold">
                      {user.name?.charAt(0)}
                    </span>
                  )}
                </button>

                <AnimatePresence>
                  {userMenuOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: 8, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: 8, scale: 0.95 }}
                      transition={{ duration: 0.15 }}
                      className="absolute right-0 top-full z-50 mt-2 w-56 rounded-xl2 border border-slate-100 dark:border-white/10 bg-white dark:bg-surface-darkCard p-2 shadow-soft-lg"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="px-3 py-2 border-b border-slate-100 dark:border-white/10 mb-1">
                        <div className="flex items-center gap-1.5">
                          <p className="text-sm font-semibold text-slate-800 dark:text-white">{user.name}</p>
                          {(user.membership?.planTier || user.membership?.tier) && (user.membership?.planTier || user.membership?.tier) !== 'free' && (
                            <span className="inline-flex items-center gap-0.5 rounded-full bg-gradient-primary px-1.5 py-0.5 text-[10px] font-semibold text-white">
                              <Crown size={9} /> {(user.membership?.planTier || user.membership?.tier) === 'premium' ? 'Premium' : 'Reader'}
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-slate-400 truncate">{user.email}</p>
                      </div>
                      <Link to="/account" onClick={() => setUserMenuOpen(false)} className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm text-slate-600 dark:text-slate-300 hover:bg-primary-50 dark:hover:bg-white/5">
                        <User size={15} /> My Account
                      </Link>
                      {(user.role === 'author' || user.pendingAuthorUpgrade) && (
                        <Link to="/publish/dashboard" onClick={() => setUserMenuOpen(false)} className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm text-slate-600 dark:text-slate-300 hover:bg-primary-50 dark:hover:bg-white/5">
                          <BookOpen size={15} /> Author Dashboard
                        </Link>
                      )}
                      <button onClick={handleLogout} className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm text-red-500 hover:bg-red-50 dark:hover:bg-red-900/10">
                        <LogOut size={15} /> Sign Out
                      </button>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ) : (
              <Link to="/login" className="hidden md:inline-flex" title="Sign in">
                <span className="flex h-10 w-10 items-center justify-center rounded-full text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-white/10">
                  <User size={19} />
                </span>
              </Link>
            )}

            <button
              onClick={() => setSidebarOpen(true)}
              aria-label="Open menu"
              title="Menu"
              className="flex h-10 w-10 items-center justify-center rounded-full text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-white/10 lg:hidden"
            >
              <Menu size={20} />
            </button>
          </div>
        </div>
      </header>

      <MobileSidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} navLinks={navLinks} user={user} onLogout={handleLogout} />
    </>
  );
}
